import type { Product } from '../types'

function stableId(category: string, name: string): string {
  const slug = name.replace(/\s+/g, '-').toLowerCase()
  return `prebuilt-${category.toLowerCase()}-${slug}`
}

const FRUITS: [string, string, number][] = [
  ['Apple', '🍎', 1.29], ['Banana', '🍌', 0.49], ['Orange', '🍊', 1.19], ['Grapes', '🍇', 2.99],
  ['Strawberry', '🍓', 3.49], ['Watermelon', '🍉', 4.99], ['Peach', '🍑', 1.79], ['Pear', '🍐', 1.49],
  ['Lemon', '🍋', 0.69], ['Cherry', '🍒', 4.29], ['Blueberry', '🫐', 3.99], ['Raspberry', '🍇', 4.49],
  ['Mango', '🥭', 1.99], ['Pineapple', '🍍', 2.99], ['Kiwi', '🥝', 0.59], ['Plum', '🍑', 2.29],
  ['Cantaloupe', '🍈', 3.49], ['Honeydew', '🍈', 3.99], ['Avocado', '🥑', 1.49], ['Coconut', '🥥', 2.49],
]
const VEGETABLES: [string, string, number][] = [
  ['Carrot', '🥕', 1.19], ['Broccoli', '🥦', 1.99], ['Lettuce', '🥬', 1.49], ['Tomato', '🍅', 1.79],
  ['Potato', '🥔', 0.99], ['Onion', '🧅', 0.89], ['Corn', '🌽', 0.39], ['Pepper', '🫑', 1.29],
  ['Cucumber', '🥒', 0.99], ['Celery', '🥬', 1.29], ['Spinach', '🥬', 2.49], ['Mushroom', '🍄', 2.29],
  ['Peas', '🫛', 1.99], ['Green Beans', '🥒', 1.79], ['Cauliflower', '🥦', 2.49], ['Sweet Potato', '🍠', 1.29],
  ['Zucchini', '🥒', 1.49], ['Cabbage', '🥬', 1.19], ['Radish', '🥬', 1.49], ['Asparagus', '🥬', 2.99],
]
const DAIRY: [string, string, number][] = [
  ['Milk', '🥛', 2.99], ['Cheese', '🧀', 4.49], ['Yogurt', '🥛', 1.29], ['Butter', '🧈', 3.49],
  ['Eggs', '🥚', 2.79], ['Cream', '🥛', 2.49], ['Cottage Cheese', '🥛', 2.99], ['Sour Cream', '🥛', 1.99],
  ['Cream Cheese', '🧀', 2.49], ['Mozzarella', '🧀', 4.99], ['Cheddar', '🧀', 4.29], ['Parmesan', '🧀', 5.49],
  ['String Cheese', '🧀', 3.99], ['Greek Yogurt', '🥛', 1.49], ['Almond Milk', '🥛', 3.49], ['Orange Juice', '🧃', 3.99],
]
const SNACKS: [string, string, number][] = [
  ['Crackers', '🍘', 2.49], ['Chips', '🥔', 2.99], ['Popcorn', '🍿', 1.99], ['Cookies', '🍪', 2.79],
  ['Pretzels', '🥨', 2.29], ['Granola Bar', '🍫', 4.99], ['Fruit Snacks', '🍬', 2.49], ['Nuts', '🥜', 4.49],
  ['Trail Mix', '🥜', 3.99], ['Goldfish', '🐟', 2.99], ['Animal Crackers', '🍪', 2.49], ['Rice Cakes', '🍘', 2.29],
  ['Cheese Crackers', '🍘', 2.79], ['Veggie Straws', '🥔', 2.99], ['Fruit Roll-Up', '🍬', 3.49], ['Pudding', '🍮', 1.29],
]
const DRINKS: [string, string, number][] = [
  ['Water', '💧', 1.00], ['Apple Juice', '🧃', 2.99], ['Grape Juice', '🧃', 2.79], ['Lemonade', '🍋', 2.49],
  ['Soda', '🥤', 1.99], ['Chocolate Milk', '🥛', 2.49], ['Iced Tea', '🍵', 1.79], ['Smoothie', '🥤', 4.49],
  ['Sports Drink', '🥤', 2.29], ['Sparkling Water', '💧', 1.49], ['Cranberry Juice', '🧃', 3.29], ['Punch', '🥤', 2.99],
  ['Hot Chocolate', '☕', 2.79], ['Capri Sun', '🧃', 3.99], ['Kool-Aid', '🥤', 1.29], ['Gatorade', '🥤', 2.49],
]
const PANTRY: [string, string, number][] = [
  ['Bread', '🍞', 2.49], ['Cereal', '🥣', 3.99], ['Oatmeal', '🥣', 2.99], ['Pasta', '🍝', 1.49],
  ['Rice', '🍚', 2.29], ['Peanut Butter', '🥜', 3.49], ['Jelly', '🍇', 2.79], ['Honey', '🍯', 4.49],
  ['Soup', '🍲', 2.29], ['Canned Beans', '🫘', 1.29], ['Tuna', '🐟', 1.99], ['Pizza', '🍕', 5.99],
  ['Mac and Cheese', '🧀', 1.29], ['Pancake Mix', '🥞', 2.99], ['Syrup', '🍯', 3.49], ['Flour', '🌾', 2.49],
  ['Sugar', '🧂', 1.99], ['Olive Oil', '🫒', 6.99], ['Ketchup', '🍅', 2.29], ['Mustard', '🫙', 1.49],
]

function buildCategory(category: string, items: [string, string, number][]): Product[] {
  return items.map(([name, emoji, price]) => {
    const id = stableId(category, name)
    return {
      id,
      name,
      imagePath: emoji,
      price,
      category,
      qrCodeValue: id,
      isPrebuilt: true,
    }
  })
}

export const PREBUILT_PRODUCTS: Product[] = [
  ...buildCategory('Fruits', FRUITS),
  ...buildCategory('Vegetables', VEGETABLES),
  ...buildCategory('Dairy', DAIRY),
  ...buildCategory('Snacks', SNACKS),
  ...buildCategory('Drinks', DRINKS),
  ...buildCategory('Pantry', PANTRY),
]
